<template>
    <div class="voice-recorder">
        <div class="voice-row">
            <div class="record-col">
                <BaseButton
                    class="record-btn"
                    :title="isRecording ? 'توقف ضبط صدا' : 'شروع به ضبط صدا'"
                    :variant="isRecording ? 'danger' : 'success'"
                    @click="toggleRecording"
                >
                    <template #icon>
                        <font-awesome-icon icon="fa-solid fa-microphone" />
                    </template>
                </BaseButton>
            </div>

            <div class="desc-col">
                <input v-model="description" placeholder="توضیحات (اختیاری)" class="desc-input" />
            </div>
        </div>

        <audio v-if="audioURL" :src="audioURL" controls></audio>
    </div>
</template>

<script setup>
import { ref } from "vue";
import BaseButton from '@/components/UiCompunents/BaseButton.vue'
import { usePatientsStore } from "@/stores/clinic/patients";
import { useToast } from "vue-toastification";

// 🧩 Props و Emits
// eslint-disable-next-line no-undef
const props = defineProps({
    recordId: { type: Number, required: true },
});
// eslint-disable-next-line no-undef
const emit = defineEmits(["uploaded", "confirm"]);
// eslint-disable-next-line no-undef
defineExpose({ confirm });

// 🔧 وضعیت‌ها
const isRecording = ref(false);
const mediaRecorder = ref(null);
const audioChunks = ref([]);
const audioBlob = ref(null);
const audioURL = ref("");
const description = ref("");
const toast = useToast();

// 🧠 استور (در صورت نیاز برای آپلود)
const patientsStore = usePatientsStore();
const { uploadPatientRecordFile } = patientsStore;

// 🎙 شروع و توقف ضبط صدا
async function toggleRecording() {
    if (!isRecording.value) {
        if (!navigator.mediaDevices || !window.MediaRecorder) {
            alert("این مرورگر ضبط صدا را پشتیبانی نمی‌کند");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // انتخاب نوع فرمت متناسب با مرورگر
            let options = { mimeType: "audio/webm" };
            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                options = { mimeType: "audio/mp4" }; // برای Safari
            }

            mediaRecorder.value = new MediaRecorder(stream, options);
            audioChunks.value = [];

            mediaRecorder.value.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) {
                    audioChunks.value.push(e.data);
                }
            };

            mediaRecorder.value.onstop = () => {
                audioBlob.value = new Blob(audioChunks.value, {
                    type: mediaRecorder.value.mimeType,
                });
                audioURL.value = URL.createObjectURL(audioBlob.value);
            };

            mediaRecorder.value.start();
            isRecording.value = true;
        } catch (err) {
            console.error("Mic error:", err);
            alert("مشکل در دسترسی به میکروفون: " + err.message);
        }
    } else {
        mediaRecorder.value.stop();
        isRecording.value = false;
    }
}

// ✅ برگرداندن فایل به والد
function confirm() {
    if (!audioBlob.value) {
        toast.error("هیچ فایل ضبط شده ای وجود ندارد");
        return null;
    }
    
    const mimeType = audioBlob.value.type;
    let ext = "webm"; // پیش‌فرض
    
    // تشخیص دقیق‌تر پسوند بر اساس MIME type
    if (mimeType.includes('webm')) {
        ext = "webm";
    } else if (mimeType.includes('mp4')) {
        // تشخیص اینکه آیا فایل صوتی MP4 است یا ویدیویی
        if (mimeType.includes('audio')) {
            ext = "m4a"; // فایل صوتی MP4
        } else {
            ext = "mp4"; // فایل ویدیویی MP4
        }
    } else if (mimeType.includes('wav')) {
        ext = "wav";
    } else if (mimeType.includes('ogg')) {
        ext = "ogg";
    } else if (mimeType.includes('mpeg')) {
        ext = "mp3";
    }
    
    const fileName = `voice_${Date.now()}.${ext}`;
    const file = new File([audioBlob.value], fileName, {
        type: mimeType,
    });

    console.log('فایل ضبط شده:', {
        mimeType: mimeType,
        fileName: fileName,
        size: audioBlob.value.size
    });

    return {
        recordId: props.recordId,
        description: description.value,
        audioFile: file,
        mimeType: mimeType
    };
}

// ❌ انصراف از ضبط
function cancel() {
    audioBlob.value = null;
    audioURL.value = "";
    description.value = "";
}

// (اختیاری) متد آپلود مستقیم
async function uploadAudio() {
    if (!audioBlob.value || !props.recordId) return;

    const fd = new FormData();
    fd.append("file", audioBlob.value, `voice.${audioBlob.value.type.split("/")[1]}`);
    fd.append("patientId", props.recordId);
    fd.append("description", description.value);

    try {
        const result = await uploadPatientRecordFile(fd);
        emit("uploaded", result.data);
        cancel();
    } catch (err) {
        console.error(err);
        alert("آپلود ناموفق بود");
    }
}
</script>

<style scoped>
.voice-recorder {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.voice-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
}

.record-col {
    flex: 0 0 auto;
}

.desc-col {
    flex: 1 1 auto;
}

@media (max-width: 768px) {
    .voice-row {
        flex-direction: column;
        align-items: stretch;
    }

    .record-col,
    .desc-col {
        width: 100%;
    }
}

/* Ensure the record button keeps BaseButton variant colors and just fills the row */
.record-btn {
    width: 100%;
    justify-content: center;
}

/* BaseButton handles the visual styling (success/danger). */

.desc-input {
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 8px;
}
</style>
